import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
  FormControl,
  FormArray,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { PartnerService } from '@feature/dashboard/partners/services/partner.service';
import { PartnerListInterface } from '@feature/dashboard/partners/types/partner-list';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { HasPlatformAccessDirective } from '@sharedModule/directives/has-platform-access.directive';
import { MaterialModule } from '@sharedModule/material/material.module';
import { Country, CountryInfo } from '@sharedModule/models/country.model';
import { selectCountry } from '@sharedModule/store/country/country.selector';
import { loadSourceCountry } from '@sharedModule/store/source-country/source-country.actions';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import { getCountryInfoFromCountryCode } from '@sharedModule/utils/array.utils';
import {
  showValidationMsg,
  removeEmptyStrings,
} from '@sharedModule/utils/form.utils';
import { Utils } from '@sharedModule/utils/util';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
  GENDER,
  DOCUMENT_TYPE,
  DOCUMENT_SECTION,
} from '../../constant/upsert-sender.constant';
import { SenderService } from '../../services/sender.service';
import { SenderListInterface } from '../../types/sender-list';
import {
  AddSenderInterface,
  SenderDocumentPayload,
  UpdateSenderInterface,
} from '../../types/upsert-sender';

@Component({
  selector: 'app-upsert-sender',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
    HasPlatformAccessDirective,
  ],
  templateUrl: './upsert-sender.component.html',
  styleUrl: './upsert-sender.component.scss',
})
export class UpsertSenderComponent implements OnInit, OnDestroy {
  public readonly attrConstant = ATTRIBUTES_CONSTANT.senders;
  public readonly gender = GENDER;
  public readonly documentType = DOCUMENT_TYPE;
  public readonly documentSection = DOCUMENT_SECTION;
  mode!: boolean;
  upsertSenderFrom!: FormGroup;
  isSuperAdmin!: boolean;
  partnerList!: Observable<PartnerListInterface[]>;
  countryList!: Observable<Country[]>;
  sourceCountryList!: Observable<CountryInfo[]>;
  senderDetails!: SenderListInterface;
  isEdit = false;
  #destroy$ = new Subject<void>();

  constructor(
    private utils: Utils,
    private store: Store,
    private router: Router,
    private fb: FormBuilder,
    private senderService: SenderService,
    private partnerService: PartnerService,
    private sessionStorageService: SessionStorageService,
    private authenticationService: AuthenticationService,
    private messageHandlerService: MessageHandlerService
  ) {
    this.isSuperAdmin = this.authenticationService.isSuperAdmin();
  }

  ngOnInit(): void {
    const authUser = this.sessionStorageService.get('userData');
    if (authUser) this.mode = JSON.parse(authUser).mode === 'ONLINE';
    this.upsertSenderFromInit(this.mode);
    if (this.isSuperAdmin) this.partnerList = this.partnerService.sharedData$;
    this.countryList = this.store.select(selectCountry);
    this.sourceCountryList = this.store.select(selectSourceCountry);
    this.isEdit = this._isEdit(this.router.url);
  }

  upsertSenderFromInit(mode: boolean): void {
    const validationArray = mode ? [Validators.required] : [];
    this.upsertSenderFrom = new FormGroup({
      [this.attrConstant.partner.key]: new FormControl(
        '',
        this.isSuperAdmin ? [Validators.required] : []
      ),
      [this.attrConstant.sourceCountry.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.firstName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.middleName.key]: new FormControl(''),
      [this.attrConstant.lastName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.nationality.key]: new FormControl('', validationArray),
      [this.attrConstant.gender.key]: new FormControl('', validationArray),
      [this.attrConstant.password.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.dob.key]: new FormControl('', [Validators.required]),
      documents: new FormArray([this.getDocumentData(mode)]),
      [this.attrConstant.address1.key]: new FormControl('', validationArray),
      [this.attrConstant.address2.key]: new FormControl('', validationArray),
      [this.attrConstant.city.key]: new FormControl('', validationArray),
      [this.attrConstant.postalCode.key]: new FormControl('', validationArray),
      [this.attrConstant.email.key]: new FormControl('', [
        Validators.required,
        Validators.pattern(REGEX_CONSTANT.email),
      ]),
      [this.attrConstant.mobileCode.key]: new FormControl({
        value: '',
        disabled: true,
      }),
      [this.attrConstant.mobileNumber.key]: new FormControl('', [
        Validators.required,
        Validators.pattern(REGEX_CONSTANT.mobileNumber),
      ]),
    });
  }

  getDocumentData(mode: boolean): FormGroup {
    const validationArray = mode ? [Validators.required] : [];
    return this.fb.group({
      [this.attrConstant.docType.key]: new FormControl('', validationArray),
      [this.attrConstant.docSection.key]: new FormControl('', validationArray),
      [this.attrConstant.docNumber.key]: new FormControl('', validationArray),
      [this.attrConstant.docExpiryDate.key]: new FormControl(
        '',
        validationArray
      ),
      [this.attrConstant.image.key]: new FormControl('', validationArray),
    });
  }

  addDocumentRow(): void {
    this.documentData.push(this.getDocumentData(this.mode));
  }

  removeDocumentRow(i: number): void {
    this.documentData.removeAt(i);
  }

  get documentData(): FormArray {
    return this.upsertSenderFrom.controls['documents'] as FormArray;
  }

  onPartnerSelected(event: { value: number }): void {
    this.store.dispatch(
      loadSourceCountry({
        payload: {
          partner_id: event.value,
        },
      })
    );
  }

  private _isEdit = (url: string): boolean => {
    const endUrl: string[] = url.split('/');
    if (endUrl[2] == 'edit') {
      this.getSenderDetails(endUrl[3]);
      this.upsertSenderFrom.controls[
        this.attrConstant.password.key
      ].clearValidators();
      this.upsertSenderFrom.controls[
        this.attrConstant.password.key
      ].updateValueAndValidity();
      return true;
    } else {
      return false;
    }
  };

  getSenderDetails(id: string): void {
    const payload = this.isSuperAdmin
      ? {
          sender_id: +id.split('-')[0],
          partner_id: +id.split('-')[1],
        }
      : { sender_id: +id };
    this.senderService
      .getSenderDetails(payload)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.senderDetails = x.response;
            if (this.isSuperAdmin)
              this.onPartnerSelected({
                value: x.response.partner_id,
              });
            const newObject = Object.assign({}, x.response);
            delete newObject.documents;
            this.upsertSenderFrom.patchValue(newObject);
            this.upsertSenderFrom.patchValue({
              [this.attrConstant.dob.key]:
                x.response.date_of_birth !== ''
                  ? new Date(this.utils.convertDate(x.response.date_of_birth))
                  : '',
            });
            this.patchValueToFormArray(x.response);
          }
        },
      });
  }

  patchValueToFormArray(data: SenderListInterface): void {
    const validationArray = this.mode ? [Validators.required] : [];
    const control = this.upsertSenderFrom.controls['documents'] as FormArray;
    control.removeAt(0);
    for (const key in data.documents!) {
      control.push(
        this.fb.group({
          [this.attrConstant.docType.key]: new FormControl(
            data.documents[key].document_type,
            validationArray
          ),
          [this.attrConstant.docSection.key]: new FormControl(
            data.documents[key].document_section,
            validationArray
          ),
          [this.attrConstant.docNumber.key]: new FormControl(
            data.documents[key].document_number,
            validationArray
          ),
          [this.attrConstant.docExpiryDate.key]: new FormControl(
            new Date(
              this.utils.convertDate(data.documents[key].document_expiry_date)
            ),
            validationArray
          ),
          // No validator: the row already has a stored scan, so re-uploading
          // stays optional. Rows added here by addDocumentRow() are built by
          // getDocumentData() and do still require one.
          [this.attrConstant.image.key]: new FormControl(''),
        })
      );
    }
  }

  onSourceCountrySelected(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    const selectedSourceCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedSourceCountry)
      this.upsertSenderFrom.patchValue({
        [this.attrConstant.mobileCode.key]: selectedSourceCountry.mobileCode,
      });
  }

  patchPassword(password: string): void {
    this.upsertSenderFrom.patchValue({
      [this.attrConstant.password.key]: password,
    });
  }

  onChooseDocument(event: string, i: number): void {
    this.documentData.controls[i].patchValue({ image: event });
  }

  onUpsertSender(): void {
    showValidationMsg(this.upsertSenderFrom);
    if (this.upsertSenderFrom.valid) {
      const formValue = this.upsertSenderFrom.getRawValue();
      formValue[this.attrConstant.dob.key] = this.utils.convertDateFormat(
        formValue[this.attrConstant.dob.key]
      );
      formValue.documents.forEach((item: SenderDocumentPayload) => {
        item.document_expiry_date = this.utils.convertDateFormat(
          item.document_expiry_date
        );
      });
      if (this.isEdit) {
        const payload = {
          sender_id: this.senderDetails.sender_id,
          ...formValue,
          documents: this.reuploadedDocuments(formValue.documents),
        };
        // Nothing re-uploaded: drop the key rather than send [], which would
        // read as "this sender has no documents".
        if (!payload.documents.length) delete payload.documents;
        this.updateSender(payload);
      } else this.addSender(formValue);
    }
  }

  /**
   * Keeps only the document rows the user re-uploaded a scan for.
   *
   * The saved scan is never patched back into the file input, so a row with an
   * empty `image` is one the user left alone. A document row is sent whole or
   * not at all - a row stripped down to its text fields would clear the stored
   * scan. removeEmptyStrings only cleans the top level of the payload, so the
   * nested rows have to be handled here.
   */
  private reuploadedDocuments(
    documents: SenderDocumentPayload[]
  ): SenderDocumentPayload[] {
    return documents.filter(document => document.image);
  }

  addSender(formValue: AddSenderInterface): void {
    this.senderService
      .addSender(removeEmptyStrings(formValue))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/sender/list']);
          }
        },
      });
  }

  updateSender(formValue: UpdateSenderInterface): void {
    this.senderService
      .updateSender(removeEmptyStrings(formValue))
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.router.navigate(['/sender/list']);
          }
        },
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
